import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Snowflake, Zap, Wind, Heart, Timer, Check } from "lucide-react";
import { useUniversalTracking } from '@/hooks/useUniversalTracking';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';

type ExerciseStep = 'select' | 'symptoms-before' | 'practice' | 'symptoms-after' | 'complete';

export const TIPPExercise = () => {
  const [step, setStep] = useState<ExerciseStep>('select');
  const [selectedTechnique, setSelectedTechnique] = useState<string | null>(null);
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const techniques = [
    {
      id: 'temperature',
      name: 'Temperature',
      icon: Snowflake,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/10',
      description: 'Change your body temperature to activate the dive reflex',
      instructions: [
        'Hold your breath',
        'Submerge your face in very cold water for 30 seconds',
        'Or hold ice cubes against your cheeks and eyes',
        'This triggers the diving reflex, slowing your heart rate'
      ],
      duration: '30 sec',
      intensity: 'High'
    },
    {
      id: 'intense-exercise',
      name: 'Intense Exercise',
      icon: Zap,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/10',
      description: 'Burn off adrenaline with intense physical activity',
      instructions: [
        'Do jumping jacks, run in place, or sprint',
        'Push-ups, burpees, or any intense movement',
        'Continue for 5-10 minutes',
        'Match the intensity to your emotion level'
      ],
      duration: '5-10 min',
      intensity: 'Very High'
    },
    {
      id: 'paced-breathing',
      name: 'Paced Breathing',
      icon: Wind,
      color: 'text-green-600',
      bgColor: 'bg-green-500/10',
      description: 'Slow your breathing to calm your nervous system',
      instructions: [
        'Breathe in slowly through your nose for 5 counts',
        'Breathe out slowly through your mouth for 7 counts',
        'Continue for 5 minutes',
        'Focus on making exhales longer than inhales'
      ],
      duration: '5 min',
      intensity: 'Low'
    },
    {
      id: 'progressive-relaxation',
      name: 'Progressive Relaxation',
      icon: Heart,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500/10',
      description: 'Systematically tense and release muscle groups',
      instructions: [
        'Start with your feet, tense for 5 seconds',
        'Release and notice the difference',
        'Move up through each muscle group',
        'Finish with your face and jaw'
      ],
      duration: '10 min',
      intensity: 'Medium'
    }
  ];

  const handleComplete = async () => {
    const technique = techniques.find(t => t.id === selectedTechnique);
    
    const success = await logExercise({
      exercise_type: 'tipp_technique',
      exercise_name: `TIPP: ${technique?.name || 'Unknown'}`,
      category: 'distress_tolerance',
      duration_minutes: 10,
      completion_data: {
        technique_used: technique?.name
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
      xp_earned: 20
    });

    if (success) {
      setStep('complete');
    }
  };

  const handleSelectTechnique = (techniqueId: string) => {
    setSelectedTechnique(techniqueId);
    setStep('symptoms-before');
  };

  if (step === 'symptoms-before') {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setStep('select');
            setSelectedTechnique(null);
          }}
          className="mb-4"
        >
          ← Back to TIPP
        </Button>
        <UniversalSymptomTracker
          mode="before"
          onRatingsChange={setSymptomsBefore}
        />
        <Button 
          onClick={() => setStep('practice')} 
          className="w-full"
          disabled={Object.keys(symptomsBefore).length === 0}
        >
          Start {techniques.find(t => t.id === selectedTechnique)?.name}
        </Button>
      </div>
    );
  }

  if (step === 'practice' && selectedTechnique) {
    const technique = techniques.find(t => t.id === selectedTechnique)!;
    const Icon = technique.icon;

    return (
      <div className="space-y-6 animate-fade-in">
        <Button
          variant="outline"
          onClick={() => setStep('select')}
          className="mb-4"
        >
          ← Back to TIPP
        </Button>

        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${technique.bgColor}`}>
                  <Icon className={`h-6 w-6 ${technique.color}`} />
                </div>
                <div>
                  <CardTitle className="text-2xl font-light">{technique.name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{technique.description}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge variant="secondary">{technique.duration}</Badge>
                <Badge className={
                  technique.intensity === 'Very High' ? 'bg-red-500/10 text-red-700' :
                  technique.intensity === 'High' ? 'bg-orange-500/10 text-orange-700' :
                  technique.intensity === 'Medium' ? 'bg-yellow-500/10 text-yellow-700' :
                  'bg-green-500/10 text-green-700'
                }>
                  {technique.intensity}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="space-y-4">
              <h3 className="font-medium">Step-by-Step Instructions:</h3>
              {technique.instructions.map((instruction, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gradient-calm rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 flex-shrink-0">
                    <span className="text-sm font-medium text-primary">{idx + 1}</span>
                  </div>
                  <p className="text-sm leading-relaxed pt-1">{instruction}</p>
                </div>
              ))}
            </div>

            {/* Safety Warning for Intense Techniques */}
            {(technique.id === 'temperature' || technique.id === 'intense-exercise') && (
              <Card className="bg-yellow-500/5 border-yellow-500/20">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Safety Note</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {technique.id === 'temperature' 
                          ? 'Do not use if you have heart conditions. Never submerge for more than 30 seconds. Stop if you feel dizzy.'
                          : 'Stop if you feel chest pain, excessive dizziness, or difficulty breathing. Consult a doctor before intense exercise if you have health concerns.'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <Button onClick={() => setStep('symptoms-after')} className="w-full">
              I've Done This-Rate How I Feel
            </Button>
          </CardContent>
        </Card>

        {/* Why This Works */}
        <Card className="bg-gradient-calm border-border/50">
          <CardContent className="py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Why This Works</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {technique.id === 'temperature' && 'The dive reflex slows your heart rate and redirects blood to vital organs, creating an immediate calming effect.'}
                {technique.id === 'intense-exercise' && 'Intense movement metabolizes stress hormones like cortisol and adrenaline, releasing the physical tension that comes with intense emotions.'}
                {technique.id === 'paced-breathing' && 'Slow, controlled breathing activates your parasympathetic nervous system (rest and digest), counteracting the fight-or-flight response.'}
                {technique.id === 'progressive-relaxation' && 'By tensing then releasing muscles, you become aware of physical tension and learn to consciously relax your body, which signals safety to your brain.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'symptoms-after') {
    return (
      <div className="space-y-4">
        <UniversalSymptomTracker
          mode="after"
          previousRatings={symptomsBefore}
          onRatingsChange={setSymptomsAfter}
        />
        <Button 
          onClick={handleComplete} 
          className="w-full"
          disabled={isLogging || Object.keys(symptomsAfter).length === 0}
        >
          {isLogging ? 'Saving...' : 'Complete Exercise (+20 XP)'}
        </Button>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <Card className="bg-gradient-calm border-emerald-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <Check className="w-6 h-6" />
            TIPP Complete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-foreground/80">
            Well done for using this skill. TIPP techniques are powerful for intense moments-
            the more you practice, the more automatic they become.
          </p>
          <Button onClick={() => {
            setStep('select');
            setSelectedTechnique(null);
            setSymptomsBefore({});
            setSymptomsAfter({});
          }} className="w-full" size="lg">
            Done
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Select technique screen (step === 'select')
  return (
    <div className="space-y-6">
      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" />
            TIPP Technique
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            TIPP is a powerful set of techniques for intense distress. Choose the one that fits your current situation. 
            These are designed for moments when emotions feel overwhelming or you need immediate relief.
          </p>
          
          <div className="grid gap-4 md:grid-cols-2">
            {techniques.map((technique) => {
              const Icon = technique.icon;
              return (
                <Card 
                  key={technique.id}
                  className="cursor-pointer hover:shadow-therapy transition-all duration-300 border-border/50"
                  onClick={() => handleSelectTechnique(technique.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${technique.bgColor}`}>
                          <Icon className={`h-5 w-5 ${technique.color}`} />
                        </div>
                        <div>
                          <h3 className="font-medium">{technique.name}</h3>
                          <p className="text-xs text-muted-foreground mt-1">{technique.description}</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center">
                      <Badge variant="secondary" className="text-xs">{technique.duration}</Badge>
                      <Button variant="ghost" size="sm">
                        Start →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
